import React, {
  useId,
  ChangeEvent,
  InputHTMLAttributes,
  TextareaHTMLAttributes,
  ForwardedRef,
} from 'react';
import {
  MaskedInput,
  createDefaultMaskGenerator,
  getCurrencyMaskGenerator,
} from 'react-hook-mask';

import InputError from './InputError';
import Label from './Label';

import {
  cn,
  LETTERS_WITH_SPECIAL_CHARACTERS,
  LETTERS_NUMBERS_WITH_SPECIAL_CHARACTERS,
} from '@/utils';
import { inputTextBaseClass } from '@/utils/constants';

interface SharedProps {
  name: string;
  value: string;
  placeholder: string;
  error?: string;
  label?: string;
  success?: boolean;
  disabled?: boolean;
  pattern?: string;
}

interface InputMaskProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  behavior: 'mask';
  mask: string;
  onChange: (name: string, value: string) => void;
}

interface CurrencyMaskProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  behavior: 'currency';
  onChange: (name: string, value: string) => void;
  maskConfig: {
    prefix?: string;
    thousandSeparator?: string;
    centsSeparator?: string;
  };
}

interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  behavior: 'default';
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onlyLetters?: boolean;
  alphaNumericSpecialCharacters?: boolean;
}

interface TextareaFieldProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  // Cambiado a TextareaHTMLAttributes para una mejor tipificación
  behavior: 'textArea';
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onlyLetters?: boolean;
  alphaNumericSpecialCharacters?: boolean;
}

type Props =
  | (SharedProps & InputMaskProps)
  | (SharedProps & InputFieldProps)
  | (SharedProps & CurrencyMaskProps)
  | (SharedProps & TextareaFieldProps);

// Función auxiliar para validar el valor basado en patrones
// Extraído para evitar duplicación de código entre input y textarea
const validateValue = (
  value: string,
  options: {
    onlyLetters?: boolean;
    alphaNumericSpecialCharacters?: boolean;
    pattern?: string;
  }
): boolean => {
  const { onlyLetters, alphaNumericSpecialCharacters, pattern } = options;

  if (onlyLetters && !LETTERS_WITH_SPECIAL_CHARACTERS.test(value)) {
    return false;
  }

  if (
    alphaNumericSpecialCharacters &&
    !LETTERS_NUMBERS_WITH_SPECIAL_CHARACTERS.test(value)
  ) {
    return false;
  }

  if (pattern) {
    const regex = new RegExp(pattern);
    if (!regex.test(value)) {
      return false;
    }
  }

  return true;
};

// eslint-disable-next-line react/display-name
const InputField = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  Props
>((props, ref) => {
  const { behavior, error, label, success, disabled, name } = props;
  const id = useId();
  const htmlForId = name;
  const alertId = useId();

  const inputClass = cn(inputTextBaseClass, {
    'border-gdt-status-red': error,
    'border-gdt-status-green bg-gdt-status-green-light': success,
    'text-gdt-gray-4 border-gdt-gray-5 bg-gdt-gray-6': disabled,
  });

  const renderInput = () => {
    if (behavior === 'default') {
      const {
        onChange,
        value,
        placeholder,
        name,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        success,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        behavior,
        type,
        onlyLetters,
        pattern,
        alphaNumericSpecialCharacters,
        ...rest
      } = props;

      return (
        <input
          ref={ref as ForwardedRef<HTMLInputElement>}
          aria-describedby={alertId}
          aria-invalid={error ? 'true' : 'false'}
          aria-label={label ? `Ingrese ${name}` : undefined}
          aria-labelledby={id}
          className={inputClass}
          disabled={disabled}
          id={htmlForId}
          name={name}
          placeholder={placeholder}
          type={type ?? 'text'}
          value={value}
          onChange={(event) => {
            // Usa la función auxiliar de validación
            if (
              !validateValue(event.target.value, {
                onlyLetters,
                alphaNumericSpecialCharacters,
                pattern,
              })
            ) {
              return;
            }
            onChange(event);
          }}
          {...rest}
        />
      );
    }

    if (behavior === 'textArea') {
      const {
        onChange,
        value,
        placeholder,
        name,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        success,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        behavior,
        onlyLetters,
        pattern,
        alphaNumericSpecialCharacters,
        ...rest
      } = props as SharedProps & TextareaFieldProps; // Cast explícito para obtener las propiedades específicas del textarea

      return (
        <textarea
          ref={ref as ForwardedRef<HTMLTextAreaElement>} // Tipado mejorado para el ref
          aria-describedby={alertId}
          aria-invalid={error ? 'true' : 'false'}
          aria-label={label ? `Ingrese ${name}` : undefined}
          aria-labelledby={id}
          className={inputClass}
          disabled={disabled}
          id={htmlForId}
          name={name}
          placeholder={placeholder}
          // Remove type prop since textarea elements don't have a type attribute
          value={value}
          onChange={(event) => {
            // Usa la misma función auxiliar de validación que input
            if (
              !validateValue(event.target.value, {
                onlyLetters,
                alphaNumericSpecialCharacters,
                pattern,
              })
            ) {
              return;
            }
            onChange(event);
          }}
          {...rest}
        />
      );
    }

    if (behavior === 'mask') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { mask, value, onChange, name, behavior, ...rest } = props;
      const maskGenerator = createDefaultMaskGenerator(mask);

      return (
        <MaskedInput
          ref={ref as ForwardedRef<HTMLInputElement>} // Tipado mejorado para el ref
          aria-describedby={alertId}
          aria-invalid={error ? 'true' : 'false'}
          aria-label={label ? `Ingrese ${name}` : undefined}
          aria-labelledby={id}
          className={inputClass}
          id={htmlForId}
          maskGenerator={maskGenerator}
          value={value}
          onChange={(val) => onChange(name, val)}
          {...rest}
        />
      );
    }

    if (behavior === 'currency') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { behavior, value, onChange, name, maskConfig, success, ...rest } =
        props;
      const maskGenerator = getCurrencyMaskGenerator(maskConfig);

      return (
        <MaskedInput
          ref={ref as ForwardedRef<HTMLInputElement>} // Tipado mejorado para el ref
          aria-describedby={alertId}
          aria-invalid={error ? 'true' : 'false'}
          aria-label={label ? `Ingrese ${name}` : undefined}
          aria-labelledby={id}
          className={inputClass}
          id={htmlForId}
          maskGenerator={maskGenerator}
          type="tel"
          value={value}
          onChange={(val) => onChange(name, val)}
          {...rest}
        />
      );
    }

    return null;
  };

  return (
    <fieldset className="space-y-1">
      <div className="space-y-[10px]">
        {label && (
          <Label htmlFor={htmlForId} id={id}>
            {label}
          </Label>
        )}
        <div className="relative">
          {renderInput()}
          {(error || success) && (
            <InputError.Icons error={error} success={success} />
          )}
        </div>
      </div>
      {error && <InputError.Label id={alertId}>{error}</InputError.Label>}
    </fieldset>
  );
});

export default React.memo(InputField);
